import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plane, Hotel, Users, Briefcase, MapPin, Headphones, Shield, CheckCircle, ArrowRight, Phone, Globe, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import InquiryForm from '../components/InquiryForm';
import SEO, { breadcrumbSchema, faqSchema } from '../components/SEO';
import Varni2FlyLogo from '../components/Varni2FlyLogo';

const dmcServices = [
  { icon: Hotel, title: 'Hotel Reservations', desc: 'Direct contracts with 200+ Thailand hotels at wholesale rates' },
  { icon: Plane, title: 'Airport Transfers', desc: '24/7 transfer service at Suvarnabhumi, Don Mueang, Phuket airports' },
  { icon: MapPin, title: 'Sightseeing Tours', desc: 'Curated city tours, temples, markets and cultural experiences' },
  { icon: Globe, title: 'Island Tours', desc: 'Phi Phi, Coral, James Bond, Krabi island packages with speedboats' },
  { icon: Users, title: 'Group Tours', desc: 'MICE and large group handling — 50 to 500+ pax capacity' },
  { icon: Briefcase, title: 'Corporate Tours', desc: 'Conferences, incentive trips, team-building events' },
  { icon: Building2, title: 'Ground Handling', desc: 'End-to-end logistics — coaches, guides, meals, permits' },
  { icon: Headphones, title: 'Local Assistance', desc: 'On-ground English/Hindi/Thai support team 7 days a week' }
];

const dmcDestinations = [
  { name: 'Pattaya', img: 'https://images.pexels.com/photos/19595559/pexels-photo-19595559.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', desc: 'Beach city — Coral Island, Walking Street, Alcazar Show' },
  { name: 'Bangkok', img: 'https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxUaGFpbGFuZCUyMGJlYWNofGVufDB8fHx8MTc4MDIzNzQ2MHww&ixlib=rb-4.1.0&q=85', desc: 'Capital — Grand Palace, Wat Pho, Chao Phraya cruise' },
  { name: 'Phuket', img: 'https://images.pexels.com/photos/19595559/pexels-photo-19595559.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', desc: 'Island paradise — Patong, Phi Phi tours, Big Buddha' },
  { name: 'Krabi', img: 'https://images.pexels.com/photos/35154841/pexels-photo-35154841.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', desc: 'Limestone cliffs — Railay Beach, Four Islands tour' }
];

const dmcFaqs = [
  { q: 'What is a DMC and why work with Varni2Fly?', a: 'A Destination Management Company (DMC) handles all on-ground services in the destination. Varni2Fly is the Thailand division of Varni Visa, providing direct local operations from Pattaya — meaning better pricing, faster confirmations, and reliable ground handling for travel agents and corporates from India.' },
  { q: 'Do you support B2B travel agents in India?', a: 'Yes. Varni2Fly offers dedicated B2B rates, white-label itineraries, agent portals (on request), markup-friendly net pricing, and 24/7 support for partner agents handling Thailand bookings.' },
  { q: 'Which Thailand cities do you operate in?', a: 'We have direct ground handling in Pattaya (HQ), Bangkok, Phuket, and Krabi. We also handle special requests for Chiang Mai, Koh Samui and Hua Hin.' },
  { q: 'Can you handle large MICE groups?', a: 'Yes. We have managed groups from 50 to 500+ pax including corporate incentives, weddings, and conferences with multi-coach logistics, dedicated tour leaders and customised gala dinners.' },
  { q: 'How do bookings work end-to-end?', a: 'Send your enquiry to Varni Visa India office or directly to varnivisa@gmail.com. Varni2Fly Thailand team confirms inventory in 24-48 hours, issues vouchers, and provides on-ground support throughout the trip.' }
];

const ThailandDMC = () => {
  const schemas = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Thailand DMC', path: '/thailand-dmc' }
    ]),
    faqSchema(dmcFaqs),
    {
      '@context': 'https://schema.org',
      '@type': 'TravelAgency',
      name: 'Varni2Fly Thailand',
      parentOrganization: { '@type': 'Organization', name: 'Varni Visa', url: 'https://www.varnivisa.com' },
      description: 'Thailand destination management company (DMC) operating from Pattaya. Hotel reservations, airport transfers, sightseeing, island tours, group tours, corporate tours, ground handling.',
      areaServed: { '@type': 'Country', name: 'Thailand' },
      url: 'https://www.varnivisa.com/thailand-dmc'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Thailand DMC - Varni2Fly | Pattaya Ground Handling, B2B Tour Operator"
        description="Varni2Fly — Thailand DMC and B2B tour operator based in Pattaya. Hotel reservations, airport transfers, sightseeing, island tours, group & corporate tour ground handling across Pattaya, Bangkok, Phuket, Krabi."
        keywords="Thailand DMC, Pattaya Tour Packages, Thailand Ground Handling, Thailand Travel Partner, Thailand B2B Tour Operator, Varni2Fly Thailand"
        path="/thailand-dmc"
        schema={schemas}
      />

      {/* Hero */}
      <section
        className="relative min-h-[520px] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.75)), url('https://images.pexels.com/photos/19595559/pexels-photo-19595559.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="container mx-auto px-4 py-16 text-white">
          <nav className="text-sm text-white/80 mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <span>Thailand DMC</span>
          </nav>
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-flex items-center gap-2 bg-[#F7945D]/20 border border-[#F7945D]/40 text-[#F7945D] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              <Sparkles size={12} /> Thailand DMC Division
            </span>
          </div>
          <div className="mb-4">
            <Varni2FlyLogo variant="light" size="lg" />
          </div>
          <h1 className="sr-only">Varni2Fly Thailand</h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mb-6">
            Direct destination management & ground handling in Pattaya, Bangkok, Phuket and Krabi.
            Your trusted Thailand B2B travel partner — better rates, reliable operations, local support.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/b2b/login">
              <Button size="lg" className="bg-[#F7945D] hover:bg-[#e67f3d]">
                B2B Agent Portal <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a href="https://wa.me/919016930091" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#25D366] hover:bg-[#1fb855]">WhatsApp B2B Desk</Button>
            </a>
            <a href="tel:+919016930091">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                <Phone size={18} className="mr-2" /> +91 90169 30091
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* About strip */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-sm mb-2">About Varni2Fly</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Thailand operations powered by Varni Visa India</h2>
              <p className="text-gray-700 mb-3">
                Varni Visa is a trusted visa and travel company based in Surat, India. Through our Thailand travel division, <strong>Varni2Fly</strong>,
                we provide direct destination management services and local support in <strong>Pattaya, Thailand</strong>.
              </p>
              <p className="text-gray-700 mb-3">
                This vertical integration allows us to offer <strong>competitive net rates</strong>, reliable service standards, and personalised travel experiences for both leisure travellers and B2B partners.
              </p>
              <p className="text-gray-700 mb-6">
                Whether you&apos;re a travel agent in India seeking a reliable Thailand handler, a corporate planning an incentive trip, or a family booking a Pattaya holiday — Varni2Fly delivers end-to-end ground handling.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-l-4 border-l-[#2D8DB4]">
                  <CardContent className="p-4">
                    <MapPin className="text-[#2D8DB4] mb-2" size={22} />
                    <h4 className="font-bold text-sm">India Office</h4>
                    <p className="text-xs text-gray-600 mt-1">105 Anupam Business Hub, Yogi Chowk, Surat, Gujarat</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-[#F7945D]">
                  <CardContent className="p-4">
                    <Globe className="text-[#F7945D] mb-2" size={22} />
                    <h4 className="font-bold text-sm">Thailand Operations</h4>
                    <p className="text-xs text-gray-600 mt-1">Pattaya, Thailand — direct ground handling team</p>
                  </CardContent>
                </Card>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxUaGFpbGFuZCUyMGJlYWNofGVufDB8fHx8MTc4MDIzNzQ2MHww&ixlib=rb-4.1.0&q=85"
                alt="Thailand"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border-l-4 border-l-[#F7945D] hidden md:block">
                <div className="text-3xl font-extrabold text-[#2D8DB4]">15,000+</div>
                <div className="text-xs text-gray-600 uppercase tracking-wider">Pax handled annually</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-sm mb-2">DMC Services</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">End-to-End Thailand Ground Handling</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">From airport pickup to send-off — Varni2Fly handles every detail of your Thailand journey</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {dmcServices.map((s, i) => {
              const Icon = s.icon;
              return (
                <Card key={i} className="hover:shadow-xl transition-all hover:-translate-y-1">
                  <CardContent className="p-5">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-lg flex items-center justify-center mb-3">
                      <Icon className="text-white" size={22} />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1">{s.title}</h3>
                    <p className="text-sm text-gray-600">{s.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-sm mb-2">Operational Cities</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Direct presence across Thailand</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {dmcDestinations.map((d, i) => (
              <div key={i} className="relative rounded-xl overflow-hidden group h-64 cursor-pointer">
                <img src={d.img} alt={d.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="text-2xl font-bold mb-1">{d.name}</h3>
                  <p className="text-xs text-white/85">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Section + Inquiry */}
      <section className="py-16 bg-gradient-to-br from-[#2D8DB4]/5 to-[#F7945D]/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-[#F7945D] font-semibold uppercase tracking-wider text-sm mb-2">For Travel Agents</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">B2B Travel Agent Support</h2>
              <p className="text-gray-700 mb-6">
                Are you a travel agent in India looking for a reliable Thailand DMC? Varni2Fly offers dedicated B2B services to help you grow your Thailand business.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  'Net rates with healthy markup room',
                  'White-label itineraries on your letterhead',
                  '24-48 hour confirmation SLA',
                  'Dedicated WhatsApp B2B desk',
                  'Custom group quotes within 24 hours',
                  'Vouchers, GST invoicing, and on-ground escalation contact'
                ].map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#2D8DB4] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-800">{it}</span>
                  </li>
                ))}
              </ul>
              <h3 className="font-bold text-xl mb-3 text-gray-900">Frequently Asked Questions</h3>
              <Accordion type="single" collapsible className="w-full">
                {dmcFaqs.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`}>
                    <AccordionTrigger className="text-left font-semibold text-sm">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-gray-600 text-sm">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            <aside className="lg:sticky lg:top-28 self-start">
              <InquiryForm
                inquiryType="tour"
                defaultSubject="Thailand DMC - Varni2Fly Inquiry"
                defaultDetails={{ source: 'thailand-dmc', division: 'Varni2Fly' }}
                title="Request a DMC Quote"
                subtitle="B2B agents, corporates & groups — get a tailored proposal in 24-48 hrs"
              />
            </aside>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-[#2D8DB4] to-[#F7945D] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">Partner with Thailand&apos;s reliable DMC</h2>
          <p className="mb-6">Powered by Varni Visa India | Operated by Varni2Fly Thailand</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#2D8DB4] hover:bg-gray-100">
                Become a Partner <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a href="mailto:varnivisa@gmail.com">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#2D8DB4]">
                varnivisa@gmail.com
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThailandDMC;
